import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../../../lib/api-client';
import { useSaveConfigFile } from '../../../hooks/useApi';

const TemplateMaintenance = () => {
    const [templates, setTemplates] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const [templateData, setTemplateData] = useState({ subject: '', html: '', text: '', teams: null });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const saveMutation = useSaveConfigFile();

    useEffect(() => {
        const fetchTemplates = async () => {
            try {
                const res = await apiClient.get('/notifications/templates');
                setTemplates(res.data || []);
            } catch (err) {
                setMessage({ type: 'error', text: `載入模板列表失敗：${err.message}` });
            }
        };

        fetchTemplates();
    }, []);

    useEffect(() => {
        if (!selectedTemplateId) return;
        const fetchTemplate = async () => {
            setLoading(true);
            setMessage(null);
            try {
                const res = await apiClient.get(`/notifications/templates/${selectedTemplateId}`);
                const data = res.data || {};
                setTemplateData({
                    subject: data.subject || '',
                    html: data.html || '',
                    text: data.text || '',
                    teams: data.teams || null
                });
            } catch (err) {
                setMessage({ type: 'error', text: `載入模板失敗：${err.message}` });
            } finally {
                setLoading(false);
            }
        };

        fetchTemplate();
    }, [selectedTemplateId]);

    const handleSave = async () => {
        if (!selectedTemplateId) return;
        try {
            await saveMutation.mutateAsync({
                path: `templates/notifications/${selectedTemplateId}.json`,
                content: {
                    subject: templateData.subject || '',
                    html: templateData.html || '',
                    text: templateData.text || '',
                    ...(templateData.teams ? { teams: templateData.teams } : {})
                }
            });
            setMessage({ type: 'success', text: '模板已儲存' });
        } catch (err) {
            setMessage({ type: 'error', text: `儲存失敗：${err.message}` });
        }
    };

    const handleTeamsChange = (value) => {
        try {
            const parsed = value ? JSON.parse(value) : null;
            setTemplateData(prev => ({ ...prev, teams: parsed }));
        } catch (err) {
            setTemplateData(prev => ({ ...prev, teams: value }));
        }
    };

    const teamsText = useMemo(() => {
        if (templateData.teams === null || templateData.teams === undefined) return '';
        if (typeof templateData.teams === 'string') return templateData.teams;
        return JSON.stringify(templateData.teams, null, 2);
    }, [templateData.teams]);

    return (
        <div className="container mx-auto px-4 py-8 max-w-7xl">
            <div className="mb-6 flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                        <span className="mr-2">📧</span>
                        信件模板維護
                    </h1>
                    <p className="text-gray-600 mt-1">管理 templates/notifications 內的郵件模板</p>
                </div>
                <Link to="/settings" className="text-sm text-blue-600 hover:underline">
                    ← 返回設定
                </Link>
            </div>

            {message && (
                <div className={`mb-4 rounded-lg p-4 ${message.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`}>
                    {message.text}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
                <div className="bg-white rounded-lg shadow-md p-4 h-fit">
                    <h2 className="text-sm font-semibold text-gray-700 mb-3">模板列表</h2>
                    <div className="space-y-2">
                        {templates.map(template => (
                            <button
                                key={template.id}
                                onClick={() => setSelectedTemplateId(template.id)}
                                className={`w-full text-left px-3 py-2 rounded-md border text-sm ${
                                    selectedTemplateId === template.id
                                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                                        : 'border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                                <div className="font-medium">{template.name}</div>
                                <div className="text-xs text-gray-500 truncate">{template.subject}</div>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-md p-6">
                    {!selectedTemplateId && (
                        <div className="text-sm text-gray-500">請先選擇模板</div>
                    )}

                    {selectedTemplateId && (
                        <>
                            <div className="flex flex-wrap gap-2 mb-4">
                                <button
                                    type="button"
                                    onClick={handleSave}
                                    disabled={saveMutation.isPending || loading}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                                >
                                    {saveMutation.isPending ? '儲存中...' : '儲存模板'}
                                </button>
                                <span className="text-xs text-gray-500 self-center">
                                    檔案：templates/notifications/{selectedTemplateId}.json
                                </span>
                            </div>

                            {loading ? (
                                <div className="flex items-center justify-center h-40">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Subject</label>
                                        <input
                                            type="text"
                                            value={templateData.subject}
                                            onChange={(e) => setTemplateData(prev => ({ ...prev, subject: e.target.value }))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5">HTML</label>
                                        <textarea
                                            rows={8}
                                            value={templateData.html}
                                            onChange={(e) => setTemplateData(prev => ({ ...prev, html: e.target.value }))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Text</label>
                                        <textarea
                                            rows={6}
                                            value={templateData.text}
                                            onChange={(e) => setTemplateData(prev => ({ ...prev, text: e.target.value }))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Teams (JSON)</label>
                                        <textarea
                                            rows={8}
                                            value={teamsText}
                                            onChange={(e) => handleTeamsChange(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm"
                                            placeholder="可留空"
                                        />
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TemplateMaintenance;
